<template>
    <div class="min-h-screen bg-[#11111b] text-white flex flex-col">
        <Header />

        <main class="relative flex items-center justify-center p-6">
            <div class="absolute inset-0 bg-cover bg-center filter blur-sm z-0"
                :style="{ backgroundImage: `url(${backgroundImage})` }">
                <div class="absolute inset-0 bg-[#11111b] opacity-50"></div>
            </div>

            <div class="relative z-10 w-full max-w-5xl mx-auto flex flex-col gap-6 py-12">
                <div class="w-full bg-[#1e1e2f] bg-opacity-80 backdrop-blur-md p-6 rounded-lg shadow-lg">
                    <div class="flex items-start justify-between mb-6">
                        <div class="pr-4 flex-1">
                            <h1 class="text-4xl font-bold mb-2">{{ blog.title }}</h1>
                            <p class="text-gray-400">{{ formattedDate }}</p>
                        </div>

                        <!-- Share button + dropdown -->
                        <div class="relative ml-4" ref="shareMenuRef">
                            <button @click="toggleShareMenu"
                                class="inline-flex items-center gap-2 px-3 py-2 bg-gray-800 bg-opacity-60 hover:bg-gray-700 rounded text-sm">
                                <Share2 />
                                <span>Share</span>
                            </button>

                            <div v-if="shareMenuOpen"
                                class="absolute right-0 mt-2 w-48 bg-[#0f1724] border border-gray-700 rounded shadow-lg z-20">
                                <button @click="shareNative()"
                                    class="w-full text-left px-3 py-2 hover:bg-gray-700 text-sm">Use device
                                    share</button>
                                <button @click="shareTo('facebook')"
                                    class="w-full text-left px-3 py-2 hover:bg-gray-700 text-sm">Facebook</button>
                                <button @click="shareTo('twitter')"
                                    class="w-full text-left px-3 py-2 hover:bg-gray-700 text-sm">Twitter</button>
                                <button @click="shareTo('linkedin')"
                                    class="w-full text-left px-3 py-2 hover:bg-gray-700 text-sm">LinkedIn</button>
                                <button @click="shareTo('whatsapp')"
                                    class="w-full text-left px-3 py-2 hover:bg-gray-700 text-sm">WhatsApp</button>
                                <button @click="shareTo('mail')"
                                    class="w-full text-left px-3 py-2 hover:bg-gray-700 text-sm">Email</button>
                                <button @click="copyLink"
                                    class="w-full text-left px-3 py-2 hover:bg-gray-700 text-sm">Copy link</button>
                                <div v-if="copySuccess" class="px-3 py-2 text-xs text-green-400">Link copied ✓</div>
                            </div>
                        </div>
                    </div>

                    <div class="w-full h-96 overflow-hidden rounded-md mb-6">
                        <img :src="blogImage" alt="Blog image" class="w-full h-full object-cover cursor-pointer"
                            @click="openPopup(blogImage)" />
                    </div>

                    <div class="prose max-w-none text-gray-100" v-html="sanitizedDescription"></div>

                    <!-- <div class="mt-6">
                        <button class="px-4 py-2 bg-cyan-600 rounded text-white" @click="$router.back()">Back</button>
                    </div> -->
                </div>
            </div>
        </main>

        <!-- Image Popup Modal -->
        <div v-if="showPopup" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            @click="closePopup">
            <div class="relative max-w-4xl max-h-[80vh] p-4" @click.stop>
                <button
                    class="absolute top-2 right-2 text-white text-2xl font-bold bg-gray-800 bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center"
                    @click="closePopup">×</button>
                <img :src="popupImage" alt="Popup Image" class="w-full h-auto max-h-[80vh] object-contain rounded-md" />
            </div>
        </div>

        <ClientFooter />
    </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { Share2 } from 'lucide-vue-next';
import { useRoute } from 'vue-router';
import Header from '@/components/Header.vue';
import ClientFooter from '@/components/ClientFooter.vue';
import { projectFirestore } from '@/firebase/config.js';
import { doc, getDoc } from 'firebase/firestore';

// Background Image
import backgroundImage from '@/assets/images/background.png';

const route = useRoute();
const blog = ref({});
const blogImage = ref('@/assets/images/default.png');

const loadBlog = async () => {
    try {
        const blogRef = doc(projectFirestore, 'blogs', route.params.id);
        const blogDoc = await getDoc(blogRef);
        if (blogDoc.exists()) {
            blog.value = blogDoc.data();
            blogImage.value = blog.value.imageURL || '@/assets/images/default.png';
            // update meta tags for social preview
            updateMetaTags();
        } else {
            console.warn('Blog not found:', route.params.id);
        }
    } catch (err) {
        console.error('Error loading blog:', err);
    }
};

// Lightweight sanitizer using DOMParser to remove script/style/iframe/object/embed and inline event handlers.
const sanitizeHtml = (dirty = '') => {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(dirty, 'text/html');

        ['script', 'style', 'iframe', 'object', 'embed'].forEach(tag => {
            const els = doc.getElementsByTagName(tag);
            Array.from(els).forEach(el => el.parentNode && el.parentNode.removeChild(el));
        });

        const all = doc.getElementsByTagName('*');
        Array.from(all).forEach(el => {
            Array.from(el.attributes || []).forEach(attr => {
                const name = attr.name.toLowerCase();
                const value = attr.value || '';
                if (name.startsWith('on')) el.removeAttribute(attr.name);
                if (name === 'href' && value.trim().toLowerCase().startsWith('javascript:')) el.removeAttribute('href');
            });
        });

        return doc.body.innerHTML;
    } catch (e) {
        return '';
    }
};

// Post-process sanitized HTML to ensure anchors open in new tab and have a highlight class
const postProcessAnchors = (html = '') => {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const anchors = doc.getElementsByTagName('a');
        Array.from(anchors).forEach(a => {
            const href = (a.getAttribute('href') || '').trim();
            if (!href.toLowerCase().startsWith('javascript:')) {
                a.setAttribute('target', '_blank');
                a.setAttribute('rel', 'noopener noreferrer');
                a.classList.add('blog-link');
            } else {
                a.removeAttribute('href');
            }
        });
        return doc.body.innerHTML;
    } catch (e) {
        return html;
    }
};

const sanitizedDescription = computed(() => postProcessAnchors(sanitizeHtml(blog.value.description || '')));

const formattedDate = computed(() => {
    if (!blog.value || !blog.value.createdAt) return '';
    try {
        const d = blog.value.createdAt.toDate ? blog.value.createdAt.toDate() : new Date(blog.value.createdAt);
        return d.toLocaleDateString();
    } catch (e) {
        return '';
    }
});

// Share menu and click-outside handling
const shareMenuOpen = ref(false);
const copySuccess = ref(false);
const shareMenuRef = ref(null);

const currentUrl = computed(() => {
    try {
        return window.location.origin + route.fullPath;
    } catch (e) {
        return '';
    }
});

const toggleShareMenu = () => {
    shareMenuOpen.value = !shareMenuOpen.value;
    copySuccess.value = false;
};

const shareNative = async () => {
    if (navigator.share) {
        try {
            await navigator.share({
                title: blog.value.title || 'Blog',
                text: (blog.value.description || '').slice(0, 120),
                url: currentUrl.value,
            });
            shareMenuOpen.value = false;
        } catch (e) {
            console.warn('Native share failed or was cancelled', e);
        }
    } else {
        console.warn('Web Share API not supported');
    }
};

const buildShareUrl = (platform) => {
    const url = encodeURIComponent(currentUrl.value || '');
    const title = encodeURIComponent(blog.value.title || '');
    const text = encodeURIComponent((blog.value.description || '').slice(0, 200));

    switch (platform) {
        case 'facebook':
            return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        case 'twitter':
            return `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        case 'linkedin':
            return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        case 'whatsapp':
            return `https://api.whatsapp.com/send?text=${title}%20${url}`;
        case 'mail':
            return `mailto:?subject=${title}&body=${text}%0A%0A${url}`;
        default:
            return '';
    }
};

const shareTo = (platform) => {
    const shareUrl = buildShareUrl(platform);
    if (!shareUrl) return;
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
    shareMenuOpen.value = false;
};

const copyLink = async () => {
    try {
        await navigator.clipboard.writeText(currentUrl.value || '');
        copySuccess.value = true;
        setTimeout(() => (copySuccess.value = false), 2500);
    } catch (e) {
        console.warn('Copy failed', e);
    }
    shareMenuOpen.value = false;
};

const onDocClick = (e) => {
    try {
        const el = shareMenuRef.value;
        if (!el) return;
        if (!el.contains(e.target)) {
            shareMenuOpen.value = false;
            copySuccess.value = false;
        }
    } catch (err) {
        // ignore
    }
};

onMounted(() => {
    loadBlog();
    document.addEventListener('click', onDocClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocClick);
});

// --- Meta tag helpers for social previews ---
const previousMeta = {};

const ensureAbsolute = (url) => {
    if (!url) return '';
    try {
        const u = new URL(url, window.location.origin);
        return u.href;
    } catch (e) {
        return url;
    }
};

const setMeta = (attrName, attrValue, content) => {
    if (!content && content !== '') return;
    let selector;
    if (attrName === 'name') selector = `meta[name="${attrValue}"]`;
    else selector = `meta[property="${attrValue}"]`;

    let el = document.head.querySelector(selector);
    if (!previousMeta[selector]) {
        previousMeta[selector] = el ? el.getAttribute('content') : null;
    }
    if (!el) {
        el = document.createElement('meta');
        if (attrName === 'name') el.setAttribute('name', attrValue);
        else el.setAttribute('property', attrValue);
        el.setAttribute('content', content || '');
        document.head.appendChild(el);
    } else {
        el.setAttribute('content', content || '');
    }
};

const setLinkCanonical = (href) => {
    const selector = 'link[rel="canonical"]';
    const el = document.head.querySelector(selector);
    if (!previousMeta[selector]) previousMeta[selector] = el ? el.getAttribute('href') : null;
    if (el) el.setAttribute('href', href);
    else {
        const link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', href);
        document.head.appendChild(link);
    }
};

const updateMetaTags = () => {
    try {
        // Prefer the post's explicit fields
        const title = (blog.value && (blog.value.title || blog.value.name)) || '';

        // prefer description, but fall back to summary/excerpt; strip HTML
        const rawDescField = blog.value && (blog.value.description || blog.value.summary || blog.value.excerpt || '');
        const descRaw = (rawDescField || '').replace(/<[^>]*>?/gm, '').trim();
        const description = descRaw.slice(0, 200);

        // Prefer the post's imageURL or image field; fallback to the component's blogImage or a site icon
        const imageField = blog.value && (blog.value.imageURL || blog.value.image || '');
        const image = ensureAbsolute(imageField || blogImage.value || '/img/icons/512.png');

        const url = ensureAbsolute(window.location.origin + route.fullPath);

        // save previous title
        if (previousMeta['document.title'] === undefined) previousMeta['document.title'] = document.title;
        document.title = title || document.title;

        // Standard meta description
        setMeta('name', 'description', description);

        // Open Graph (use post fields)
        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:image', image);
        setMeta('property', 'og:url', url);
        setMeta('property', 'og:type', 'article');

        // Twitter
        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', title);
        setMeta('name', 'twitter:description', description);
        setMeta('name', 'twitter:image', image);

        // canonical
        setLinkCanonical(url);
    } catch (e) {
        console.warn('Failed to update meta tags', e);
    }
};

const restoreMetaTags = () => {
    try {
        // restore document title
        if (previousMeta['document.title'] !== undefined) document.title = previousMeta['document.title'];
        // restore/cleanup meta tags
        Object.keys(previousMeta).forEach((selector) => {
            if (selector === 'document.title') return;
            const prev = previousMeta[selector];
            const el = document.head.querySelector(selector);
            if (el) {
                if (prev === null) {
                    // it didn't exist before - remove
                    el.parentNode && el.parentNode.removeChild(el);
                } else {
                    el.setAttribute('content', prev);
                    // if it was a link[rel="canonical"] previousMeta stored href
                    if (el.tagName.toLowerCase() === 'link' && el.getAttribute('rel') === 'canonical') el.setAttribute('href', prev);
                }
            }
        });
    } catch (e) {
        // ignore
    }
};

onBeforeUnmount(() => {
    // remove click listener already removed above; also restore meta tags
    restoreMetaTags();
});

// Image popup state and handlers
const showPopup = ref(false);
const popupImage = ref('');

const openPopup = (image) => {
    popupImage.value = image || blogImage.value || '@/assets/images/default.png';
    showPopup.value = true;
};

const closePopup = () => {
    showPopup.value = false;
    popupImage.value = '';
};
</script>

<style scoped>
@media (max-width: 640px) {
    .h-96 {
        height: 24rem;
    }
}

/* Ensure styles apply to HTML inserted via v-html using deep selector */
.prose ::v-deep a {
    color: #25aceb;
    /* blue-600 */
}

/* highlighted blog links */
::v-deep .blog-link {
    color: #25aceb;
    /* blue-600 */
    background-color: rgba(37, 189, 235, 0.12);
    /* translucent blue */
    padding: 0.18rem 0.8rem;
    /* increased horizontal padding */
    border-radius: 0.375rem;
    /* 6px */
    text-decoration: none;
    transition: background-color 150ms ease, color 150ms ease;
}

::v-deep .blog-link:hover,
::v-deep .blog-link:focus {
    background-color: rgba(37, 162, 235, 0.2);
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

/* Share dropdown tweaks */
.share-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.share-dropdown {
    min-width: 12rem;
}
</style>